import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { emailApi, tasksApi } from "@/lib/api";
import { toast } from "sonner";

export default function EmailDialog({ open, setOpen, contact, entityId, projectId, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [followup, setFollowup] = useState(false);
  const [followupDate, setFollowupDate] = useState("");

  useEffect(() => {
    if (open) {
      emailApi.templates().then(r => setTemplates(r.data)).catch(()=>{});
      setSubject(""); setBody(""); setTemplateId(""); setFollowup(false); setFollowupDate("");
    }
  }, [open]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find(x => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.html_content); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body required");
    if (!contact?.email) return toast.error("Contact has no email");
    setSending(true);
    try {
      await emailApi.send({
        contact_id: contact.id, entity_id: entityId, project_id: projectId,
        subject, html_content: body, template_id: templateId || null,
      });
      if (followup && followupDate) {
        await tasksApi.create({
          entity_id: entityId, project_id: projectId || null,
          name: `Follow up: ${contact.full_name}`,
          description: `Follow up on: ${subject}`,
          due_date: followupDate, priority: "Medium", status: "Open",
          assignee_contact_id: contact.id,
        });
      }
      toast.success("Email sent");
      onSent?.();
    } catch(e){ toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-dialog">
        <DialogHeader><DialogTitle className="font-display">Email {contact.full_name}</DialogTitle></DialogHeader>
        <div className="py-2 space-y-4">
          <div className="text-sm text-white/60">To: <span className="text-white/90">{contact.email}</span></div>
          {templates.length > 0 && (
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Template (optional)</Label>
              <Select value={templateId || "__none"} onValueChange={(v) => applyTemplate(v === "__none" ? "" : v)}>
                <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue placeholder="Choose template"/></SelectTrigger>
                <SelectContent className="bg-[#151A23] border-white/10 text-white">
                  <SelectItem value="__none">No template</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Subject</Label><Input data-testid="email-subject-input" value={subject} onChange={e=>setSubject(e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Body (HTML supported)</Label><Textarea data-testid="email-body-input" rows={10} value={body} onChange={e=>setBody(e.target.value)} className="mt-1.5 bg-white/5 border-white/10 font-mono text-sm"/></div>
          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={followup} onChange={e=>setFollowup(e.target.checked)} className="w-4 h-4 accent-white"/>
              <span className="text-sm">Create follow-up task after sending</span>
            </label>
            {followup && (
              <Input type="date" value={followupDate} onChange={e=>setFollowupDate(e.target.value)} className="mt-2 bg-white/5 border-white/10"/>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="email-send-btn" className="rounded-full bg-white text-black hover:bg-white/90">{sending?"Sending…":"Send email"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
